import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, BookUser, Wrench, Package, Box, Building2, ChevronRight, Pencil, Trash2, Check, Users, Globe, ChevronDown, ChevronUp, Tag, Infinity, Save, AlertCircle, Bell, BellOff, Lock, Unlock } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../api';
import FonctionsInternes from '../FonctionsInternes';
import { subscribePush, unsubscribePush, isPushSupported, getPushPermission, isPushSubscribed } from '../../utils/pushNotifications';

const TABS = [
  { key: 'annuaire',  label: 'Annuaire',            icon: BookUser },
  { key: 'materiels', label: 'Matériels',            icon: Wrench },
  { key: 'atelier',   label: 'Atelier',              icon: Wrench },
  { key: 'catalogue', label: 'Catalogue',            icon: Package },
  { key: 'location',  label: 'Location',             icon: Box },
  { key: 'commerce',  label: 'Commerce',             icon: Building2 },
  { key: 'rh',        label: 'Ressources humaines',  icon: Users },
];
// ─── Helpers badges tags ──────────────────────────────────────────────────────
function TagPorteeBadge({ tag }) {
  // Party Model : pour_tiers = client global (B2B entreprise ou B2C particulier)
  const pourTiers = tag.pour_tiers ?? tag.pour_entreprise;
  if (pourTiers && tag.pour_personne) return <span className="text-xs" style={{ color: '#64748B' }}>Client + Contact</span>;
  if (pourTiers) return <span className="text-xs font-medium" style={{ color: '#1D4ED8' }}>Client</span>;
  if (tag.pour_personne) return <span className="text-xs font-medium" style={{ color: '#6D28D9' }}>Contact</span>;
  return <span>—</span>;
}

const TAG_COLORS_LIST = ['#6B7280','#0369A1','#B45309','#15803D','#7C3AED','#DB2777','#EA580C','#0891B2'];

const TAG_TYPE_OPTIONS = [
  { value: 'fonction',      label: 'Fonctions',        portee: 'Contacts',             pour_personne: true,  pour_entreprise: false, pour_tiers: false },
  { value: 'automation',    label: 'Automatisation',   portee: 'Contacts',             pour_personne: true,  pour_entreprise: false, pour_tiers: false },
  { value: 'qualification', label: 'Qualification',    portee: 'Contacts & Clients',   pour_personne: true,  pour_entreprise: true,  pour_tiers: true  },
  { value: 'statut',        label: 'Statut',           portee: 'Clients (B2B & B2C)',  pour_personne: false, pour_entreprise: true,  pour_tiers: true  },
];

// Groupes de tags : ordre d'affichage + métadonnées visuelles
const TAG_GROUPS = [
  { key: 'statut',        label: 'Statuts client',        desc: 'Statuts financiers et légaux (B2B & B2C)',  iconColor: '#0369A1', bg: '#EFF6FF' },
  { key: 'automation',    label: 'Automatisation e-mail', desc: 'Déclenchent des envois automatiques',       iconColor: '#0891B2', bg: '#ECFEFF' },
  { key: 'fonction',      label: 'Fonctions',              desc: 'Rôles métier des contacts',                 iconColor: '#7C3AED', bg: '#F5F3FF' },
  { key: 'qualification', label: 'Qualifications',         desc: 'Catégories libres contacts et entreprises', iconColor: '#15803D', bg: '#F0FDF4' },
  { key: null,            label: 'Personnalisés',           desc: 'Tags sans catégorie',                      iconColor: '#6B7280', bg: '#F8FAFC' },
];

// ─── Side panel création / édition de tag ────────────────────────────────────
function TagSidePanel({ panel, onClose, onSave, onSaveUsers, onDelete, saving }) {
  const isEdit    = panel?.mode === 'edit';
  const isSysteme = isEdit && panel.tag?.est_systeme;

  const [activeTab, setActiveTab] = React.useState('details');

  const [form, setForm] = React.useState(() =>
    isEdit
      ? { nom: panel.tag.nom, description: panel.tag.description || '', tag_type: panel.tag.tag_type || 'fonction', couleur: panel.tag.couleur || '#6B7280' }
      : { nom: '', description: '', tag_type: 'fonction', couleur: '#6B7280' }
  );

  // ── Contrôle d'accès ────────────────────────────────────────────────────────
  const [allUsers,        setAllUsers]        = React.useState([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState([]);
  const [usersLoading,    setUsersLoading]    = React.useState(false);

  React.useEffect(() => {
    api.getUsers().then(r => setAllUsers((r.data || []).filter(u => u.actif))).catch(() => {});
    if (isEdit && panel.tag?.tag_id) {
      setUsersLoading(true);
      api.getTagUsers(panel.tag.tag_id)
        .then(r => setSelectedUserIds((r.data || []).map(u => u.user_id)))
        .catch(() => {})
        .finally(() => setUsersLoading(false));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleUser = (userId) =>
    setSelectedUserIds(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);

  const typeOpt  = TAG_TYPE_OPTIONS.find(t => t.value === form.tag_type) || TAG_TYPE_OPTIONS[0];
  const isPublic = selectedUserIds.length === 0;

  const handleSaveDetails = () => {
    if (!form.nom.trim() || isSysteme) return;
    onSave({
      nom: form.nom.trim(), description: form.description.trim() || null,
      couleur: form.couleur, tag_type: form.tag_type,
      pour_personne: typeOpt.pour_personne, pour_entreprise: typeOpt.pour_entreprise, pour_tiers: typeOpt.pour_tiers,
      _user_ids: selectedUserIds,
    });
  };

  const handleSaveAccess = () => {
    if (isEdit) onSaveUsers(panel.tag.tag_id, selectedUserIds);
  };

  const TABS = [
    { key: 'details', label: 'Détails' },
    { key: 'acces',   label: 'Contrôle d\'accès' },
  ];

  return (
    <>
      <div className="fixed inset-0 z-40" onClick={onClose} style={{ background: 'rgba(15,23,42,0.25)' }} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-96 flex flex-col shadow-2xl"
        style={{ background: '#ffffff', borderLeft: '1px solid var(--border)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--border)' }}>
          <div>
            <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>
              {isEdit ? 'Modifier le tag' : 'Nouveau tag'}
            </p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              {isEdit ? panel.tag.nom : 'Créer un tag personnalisé'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" data-testid="tag-panel-close">
            <X size={15} style={{ color: 'var(--text-muted)' }} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: 'var(--border)' }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex-1 px-4 py-2.5 text-xs font-semibold transition-colors"
              style={{
                color: activeTab === tab.key ? 'var(--primary-solid)' : 'var(--text-muted)',
                borderBottom: activeTab === tab.key ? '2px solid var(--primary-solid)' : '2px solid transparent',
                background: 'transparent',
              }}>
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Onglet Détails ── */}
        {activeTab === 'details' && (
          <>
            {isSysteme && (
              <div className="mx-5 mt-4 flex items-start gap-2 p-3 rounded-lg text-xs"
                style={{ background: '#FEF3C7', border: '1px solid #FDE68A' }}>
                <span style={{ fontSize: 14 }}>🔒</span>
                <span style={{ color: '#92400E' }}>Ce tag est un tag système et ne peut pas être modifié.</span>
              </div>
            )}
            <div className={`flex-1 overflow-y-auto px-5 py-5 space-y-5${isSysteme ? ' opacity-50 pointer-events-none' : ''}`}>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Nom</label>
                <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
                  placeholder="Nom du tag…"
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  style={{ borderColor: 'var(--border)' }} autoFocus data-testid="tag-panel-nom" />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Description</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Description courte (optionnel)…"
                  className="w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30"
                  style={{ borderColor: 'var(--border)' }} data-testid="tag-panel-desc" />
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Catégorie</label>
                <div className="space-y-2">
                  {TAG_TYPE_OPTIONS.map(opt => (
                    <button key={opt.value} onClick={() => setForm(f => ({ ...f, tag_type: opt.value }))}
                      data-testid={`tag-type-${opt.value}`}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 transition-all text-left"
                      style={{
                        borderColor: form.tag_type === opt.value ? 'var(--primary-solid)' : 'var(--border)',
                        background:  form.tag_type === opt.value ? '#EFF6FF' : 'transparent',
                      }}>
                      <div className="flex-1">
                        <p className="text-sm font-semibold" style={{ color: form.tag_type === opt.value ? 'var(--primary-solid)' : 'var(--text)' }}>{opt.label}</p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Portée : {opt.portee}</p>
                      </div>
                      {form.tag_type === opt.value && (
                        <div className="w-4 h-4 rounded-full flex items-center justify-center" style={{ background: 'var(--primary-solid)' }}>
                          <Check size={10} style={{ color: 'white' }} />
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>Couleur</label>
                <div className="flex flex-wrap gap-2">
                  {TAG_COLORS_LIST.map(c => (
                    <button key={c} onClick={() => setForm(f => ({ ...f, couleur: c }))}
                      data-testid={`tag-color-${c}`}
                      className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
                      style={{ background: c, borderColor: form.couleur === c ? '#1E293B' : 'transparent' }} />
                  ))}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="w-5 h-5 rounded-full flex-shrink-0" style={{ background: form.couleur }} />
                  <span className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>{form.couleur}</span>
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t flex flex-col gap-2" style={{ borderColor: 'var(--border)' }}>
              {isEdit && onDelete && !isSysteme && (
                <button onClick={() => onDelete(panel.tag.tag_id)}
                  className="text-xs px-3 py-2 rounded-lg border text-red-600 hover:bg-red-50 transition-colors"
                  style={{ borderColor: '#FECACA' }} data-testid="tag-panel-delete">
                  Supprimer ce tag
                </button>
              )}
              <div className="flex gap-2">
                <button onClick={onClose} className="flex-1 text-sm px-3 py-2 rounded-lg border"
                  style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                  {isSysteme ? 'Fermer' : 'Annuler'}
                </button>
                {!isSysteme && (
                  <button onClick={handleSaveDetails} disabled={saving || !form.nom.trim()}
                    className="flex-1 text-sm px-3 py-2 rounded-lg text-white font-medium disabled:opacity-50"
                    style={{ background: 'var(--primary-solid)' }} data-testid="tag-panel-save">
                    {saving ? '…' : isEdit ? 'Enregistrer' : 'Créer'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── Onglet Contrôle d'accès ── */}
        {activeTab === 'acces' && (
          <>
            <div className="flex-1 overflow-hidden flex flex-col px-5 pt-5 pb-0 gap-3">
              <div className="flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-1.5">
                  {isPublic
                    ? <Unlock size={13} style={{ color: '#16A34A' }} />
                    : <Lock   size={13} style={{ color: '#DC2626' }} />}
                  <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                    Utilisateurs autorisés
                  </span>
                </div>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                  style={{ background: isPublic ? '#DCFCE7' : '#FEE2E2', color: isPublic ? '#16A34A' : '#DC2626' }}>
                  {isPublic ? 'Public' : `${selectedUserIds.length} utilisateur${selectedUserIds.length > 1 ? 's' : ''}`}
                </span>
              </div>
              <p className="text-xs flex-shrink-0" style={{ color: 'var(--text-muted)' }}>
                Aucun utilisateur sélectionné = tag accessible à tous les rôles. Cochez pour restreindre aux utilisateurs listés (les admins contournent toujours cette restriction).
              </p>
              {usersLoading ? (
                <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</p>
              ) : (
                <div className="flex-1 overflow-y-auto pr-1 space-y-1.5 pb-5">
                  {allUsers.map(u => (
                    <label key={u.user_id}
                      className="flex items-center gap-2.5 px-2 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                      data-testid={`tag-user-${u.user_id}`}>
                      <input type="checkbox" className="w-3.5 h-3.5 accent-blue-600 flex-shrink-0"
                        checked={selectedUserIds.includes(u.user_id)}
                        onChange={() => toggleUser(u.user_id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: 'var(--text)' }}>{u.name}</p>
                        <p className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>{u.email}</p>
                      </div>
                    </label>
                  ))}
                  {allUsers.length === 0 && (
                    <p className="text-xs text-center py-2" style={{ color: 'var(--text-muted)' }}>Aucun utilisateur trouvé</p>
                  )}
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t flex gap-2 flex-shrink-0" style={{ borderColor: 'var(--border)' }}>
              <button onClick={onClose} className="flex-1 text-sm px-3 py-2 rounded-lg border"
                style={{ borderColor: 'var(--border)', color: 'var(--text-muted)' }}>
                Annuler
              </button>
              {isEdit && (
                <button onClick={handleSaveAccess} disabled={saving}
                  className="flex-1 text-sm px-3 py-2 rounded-lg text-white font-medium disabled:opacity-50"
                  style={{ background: 'var(--primary-solid)' }} data-testid="tag-panel-save-access">
                  {saving ? '…' : 'Enregistrer'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ─── Onglet Annuaire ──────────────────────────────────────────────────────────
function AnnuaireTab({ parametres, onSave }) {
  // — Section Tags —
  const [allTags,     setAllTags]     = useState([]);
  const [tagsLoading, setTagsLoading] = useState(true);
  const [tagSaving,   setTagSaving]   = useState(false);
  const [sidePanel,   setSidePanel]   = useState(null); // null | {mode:'new'} | {mode:'edit', tag:{...}}

  useEffect(() => {
    api.getAnnuaireTags().then(r => setAllTags(r.data || [])).catch(() => {}).finally(() => setTagsLoading(false));
  }, []);

  const reloadTags = async () => {
    const r = await api.getAnnuaireTags();
    setAllTags(r.data || []);
  };

  const handleSavePanel = async (data) => {
    setTagSaving(true);
    try {
      let tagId;
      if (sidePanel?.mode === 'edit') {
        await api.updateAnnuaireTag(sidePanel.tag.tag_id, data);
        tagId = sidePanel.tag.tag_id;
        toast.success('Tag mis à jour');
      } else {
        const r = await api.createAnnuaireTag(data);
        tagId = r.data?.tag_id;
        toast.success('Tag créé');
      }
      // Sauvegarde du contrôle d'accès (tag_users)
      if (tagId && Array.isArray(data._user_ids)) {
        await api.putTagUsers(tagId, data._user_ids);
      }
      setSidePanel(null);
      await reloadTags();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setTagSaving(false); }
  };

  const handleDeleteTag = async (tagId) => {
    const tag = allTags.find(t => t.tag_id === tagId);
    if (!window.confirm(`Supprimer le tag "${tag?.nom || tagId}" ?\n\nIl sera retiré de toutes les fiches de votre organisation.`)) return;
    try {
      await api.deleteAnnuaireTag(tagId);
      setSidePanel(null);
      await reloadTags();
      toast.success('Tag supprimé');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur suppression tag'); }
  };

  // Sauvegarde contrôle d'accès seul (tags système ou depuis l'onglet Contrôle d'accès)
  const handleSaveUsersOnly = async (tagId, userIds) => {
    setTagSaving(true);
    try {
      await api.putTagUsers(tagId, userIds);
      toast.success('Contrôle d\'accès mis à jour');
      setSidePanel(null);
      await reloadTags();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setTagSaving(false); }
  };

  return (
    <div className="space-y-6">
      {/* ── Catalogue de tags ─────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border)' }}>
        {/* En-tête */}
        <div className="px-6 pt-5 pb-4 flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: 'var(--text)' }}>
              <Tag size={14} style={{ color: 'var(--primary-solid)' }} /> Catalogue de tags
            </h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Tags de catégorisation des entreprises et contacts. Les tags système (🔒) sont en lecture seule.
            </p>
          </div>
          <button
            onClick={() => setSidePanel({ mode: 'new' })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium text-white"
            style={{ background: 'var(--primary-solid)' }}
            data-testid="new-tag-btn">
            <Plus size={13} /> Nouveau
          </button>
        </div>

        <div className="border-t" style={{ borderColor: 'var(--border)' }}>
          {tagsLoading ? (
            <div className="px-6 py-4 text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</div>
          ) : (
            <>
              {/* ── En-tête colonnes ── */}
              <div className="hidden md:grid px-4 py-2 text-xs font-semibold uppercase tracking-wide border-b select-none"
                style={{
                  gridTemplateColumns: '20px 180px 1fr 140px 64px',
                  gap: '12px',
                  borderColor: 'var(--border)',
                  background: 'var(--bg-subtle)',
                  color: 'var(--text-muted)',
                }}>
                <span />
                <span>Nom</span>
                <span>Description</span>
                <span>Portée</span>
                <span />
              </div>

              {/* ── Tags groupés par type ── */}
              {TAG_GROUPS.map(group => {
                const tagsInGroup = allTags.filter(t => t.tag_type === group.key);
                if (tagsInGroup.length === 0) return null;
                return (
                  <div key={String(group.key)} className="border-b last:border-b-0" style={{ borderColor: 'var(--border)' }}>
                    {/* En-tête de groupe */}
                    <div className="flex items-center gap-2 px-4 py-2"
                      style={{ background: group.bg, borderBottom: `1px solid var(--border)` }}>
                      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: group.iconColor }} />
                      <span className="text-xs font-semibold" style={{ color: group.iconColor }}>{group.label}</span>
                      <span className="text-xs" style={{ color: '#94A3B8' }}>— {group.desc}</span>
                      <span className="ml-auto text-xs rounded-full px-1.5 py-0.5 font-medium"
                        style={{ background: group.iconColor + '22', color: group.iconColor }}>
                        {tagsInGroup.length}
                      </span>
                    </div>

                    {/* Lignes du groupe */}
                    <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                      {tagsInGroup.map(tag => (
                        <div key={tag.tag_id}
                          className="grid px-4 py-2.5 items-center hover:bg-gray-50 transition-colors"
                          style={{ gridTemplateColumns: '20px 234px 1fr 140px 64px', gap: '12px' }}>
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: tag.couleur }} />
                          <span className="text-sm font-semibold truncate" style={{ color: 'var(--text)' }}>
                            {tag.nom}
                          </span>
                          <span className="text-xs truncate" style={{ color: 'var(--text-muted)' }}>
                            {tag.description || <span style={{ color: 'var(--border)' }}>—</span>}
                          </span>
                          <TagPorteeBadge tag={tag} />
                          <div className="flex items-center gap-1 justify-end">
                            {tag.est_systeme ? (
                              <button
                                onClick={() => setSidePanel({ mode: 'edit', tag })}
                                className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                title="Configurer le contrôle d'accès"
                                data-testid={`tag-edit-btn-${tag.tag_id}`}>
                                <Lock size={12} style={{ color: '#94A3B8' }} />
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={() => setSidePanel({ mode: 'edit', tag })}
                                  className="p-1.5 rounded hover:bg-gray-100 transition-colors"
                                  data-testid={`tag-edit-btn-${tag.tag_id}`}>
                                  <Pencil size={12} style={{ color: 'var(--text-muted)' }} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
              {allTags.length === 0 && (
                <div className="px-6 py-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>
                  Aucun tag — cliquez sur "Nouveau" pour commencer.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Side Panel tag ── */}
      {sidePanel && (
        <TagSidePanel
          panel={sidePanel}
          onClose={() => setSidePanel(null)}
          onSave={handleSavePanel}
          onSaveUsers={handleSaveUsersOnly}
          onDelete={handleDeleteTag}
          saving={tagSaving}
        />
      )}
    </div>
  );
}

// ─── Onglet Catalogue — Matrices de prix de vente ────────────────────────────

function CatalogueTab() {
  const [matrices, setMatrices]         = useState([]);
  const [familles, setFamilles]         = useState([]);
  const [sousFamilles, setSousFamilles] = useState([]);
  const [marques, setMarques]           = useState([]);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [expandedId, setExpandedId]     = useState(null);
  const [dirty, setDirty]               = useState(false);

  useEffect(() => {
    Promise.all([
      api.getMatricesPrix(),
      api.getFamilles({ per_page: 200 }),
      api.getSousFamilles({ per_page: 500 }),
      api.getMarques({ per_page: 500 }),
    ]).then(([m, f, sf, mrk]) => {
      setMatrices(m.data || []);
      setFamilles(f.data?.items || f.data || []);
      setSousFamilles(sf.data?.items || sf.data || []);
      setMarques(mrk.data?.items || mrk.data || []);
    }).catch(() => toast.error('Erreur chargement données')).finally(() => setLoading(false));
  }, []);

  const markDirty = (updater) => {
    setDirty(true);
    updater();
  };

  const save = async () => {
    setSaving(true);
    try {
      // Normaliser les lignes : prix_max undefined → null (fallback) avant l'envoi
      const normalized = matrices.map(m => ({
        ...m,
        lignes: (m.lignes || []).map(l => ({
          ...l,
          prix_max: (l.prix_max === undefined || l.prix_max === '') ? null : l.prix_max,
        })),
      }));
      const res = await api.updateMatricesPrix(normalized);
      setMatrices(res.data);
      setDirty(false);
      toast.success('Matrices enregistrées');
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const addMatrice = () => {
    const newId = `MAT-${Math.random().toString(36).slice(2, 12)}`;
    const newM = {
      matrice_id: newId,
      nom: 'Nouvelle matrice',
      ordre: matrices.length,
      criteres: { cp_prefixes: [], famille_id: null, sous_famille_id: null, marque_id: null },
      lignes: [{ prix_min: 0, prix_max: null, coeff: 1.0 }],
    };
    markDirty(() => setMatrices(prev => [...prev, newM]));
    setExpandedId(newId);
  };

  const removeMatrice = (id) => {
    markDirty(() => setMatrices(prev => prev.filter(m => m.matrice_id !== id)));
    if (expandedId === id) setExpandedId(null);
  };

  const updateMatrice = (id, updates) => {
    markDirty(() => setMatrices(prev => prev.map(m => m.matrice_id === id ? { ...m, ...updates } : m)));
  };

  const moveMatrice = (idx, dir) => {
    const newM = [...matrices];
    const swapIdx = idx + dir;
    if (swapIdx < 0 || swapIdx >= newM.length) return;
    [newM[idx], newM[swapIdx]] = [newM[swapIdx], newM[idx]];
    markDirty(() => setMatrices(newM));
  };

  const updateCritere = (id, field, value) => {
    markDirty(() => setMatrices(prev => prev.map(m => m.matrice_id === id
      ? { ...m, criteres: { ...(m.criteres || {}), [field]: value } }
      : m
    )));
  };

  const addLigne = (id) => {
    markDirty(() => setMatrices(prev => prev.map(m => {
      if (m.matrice_id !== id) return m;
      const lignes = [...(m.lignes || [])];
      // Fallback = uniquement prix_max === null (strict)
      const fallbackIdx = lignes.findIndex(l => l.prix_max === null);
      // Nouvelle ligne intermédiaire : prix_max non défini (input vide, à renseigner)
      const newLigne = { prix_min: 0, prix_max: undefined, coeff: 1.0 };
      if (fallbackIdx !== -1) {
        lignes.splice(fallbackIdx, 0, newLigne);
      } else {
        lignes.push(newLigne);
      }
      return { ...m, lignes };
    })));
  };

  const removeLigne = (matriceId, ligneIdx) => {
    markDirty(() => setMatrices(prev => prev.map(m => {
      if (m.matrice_id !== matriceId) return m;
      const lignes = [...(m.lignes || [])];
      lignes.splice(ligneIdx, 1);
      return { ...m, lignes };
    })));
  };

  const updateLigne = (matriceId, ligneIdx, field, value) => {
    markDirty(() => setMatrices(prev => prev.map(m => {
      if (m.matrice_id !== matriceId) return m;
      const lignes = [...(m.lignes || [])];
      lignes[ligneIdx] = { ...lignes[ligneIdx], [field]: value };
      return { ...m, lignes };
    })));
  };

  const getSousFamillesForFam = (familleId) => {
    if (!familleId) return sousFamilles;
    return sousFamilles.filter(sf => sf.famille_id === familleId);
  };

  const inpSm = "px-2 py-1 rounded-lg border text-xs focus:outline-none focus:ring-1";

  if (loading) return (
    <div className="py-12 text-center">
      <div className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin mx-auto"
        style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
    </div>
  );

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="bg-white rounded-xl border p-5" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between">
          <div>
            <h3 className="font-semibold text-sm" style={{ color: 'var(--text)',  }}>
              Matrices de prix de vente
            </h3>
            <p className="text-xs mt-0.5 max-w-xl" style={{ color: 'var(--text-muted)' }}>
              Calculez automatiquement le prix de vente depuis le prix d'achat selon l'origine du produit (CP fournisseur), sa famille, sous-famille ou marque.
              Les critères d'une même matrice sont liés par <strong>ET</strong>. La <strong>première matrice</strong> dont tous les critères correspondent est appliquée.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
            {dirty && (
              <span className="flex items-center gap-1 text-xs px-2 py-1 rounded-full"
                style={{ background: '#FEF3C7', color: '#92400E' }}>
                <AlertCircle size={11} /> Modifications non sauvegardées
              </span>
            )}
            <button onClick={save} disabled={saving || !dirty}
              data-testid="save-matrices-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-40"
              style={{ background: 'var(--primary-solid)' }}>
              {saving
                ? <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                : <Save size={12} />}
              Enregistrer
            </button>
            <button onClick={addMatrice}
              data-testid="add-matrice-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-white"
              style={{ background: 'var(--primary-gradient)' }}>
              <Plus size={12} /> Nouvelle matrice
            </button>
          </div>
        </div>

        {/* Info règle */}
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg text-xs"
          style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
          <AlertCircle size={13} style={{ color: 'var(--text-muted)', flexShrink: 0, marginTop: 1 }} />
          <div style={{ color: 'var(--text-muted)' }}>
            Chaque matrice doit contenir au moins une ligne avec <strong>Prix max = ∞</strong> (fallback) pour garantir qu'un prix sera toujours calculé.
            Utilisez les flèches pour définir l'ordre de priorité — la matrice en tête de liste est évaluée en premier.
          </div>
        </div>
      </div>

      {/* Liste des matrices */}
      {matrices.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
            style={{ background: 'var(--primary-50)' }}>
            <Tag size={20} style={{ color: 'var(--primary-solid)' }} />
          </div>
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>Aucune matrice configurée</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
            Cliquez sur "Nouvelle matrice" pour commencer.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {matrices.map((matrice, idx) => {
            const isExpanded = expandedId === matrice.matrice_id;
            const criteres = matrice.criteres || {};
            const famNom = familles.find(f => f.famille_id === criteres.famille_id)?.nom;
            const sfNom = sousFamilles.find(sf => sf.sous_famille_id === criteres.sous_famille_id)?.nom;
            const mrkNom = marques.find(m => m.marque_id === criteres.marque_id)?.nom;
            const cpStr = (criteres.cp_prefixes || []).join(', ');
            const hasCriteres = cpStr || criteres.famille_id || criteres.sous_famille_id || criteres.marque_id;
            const sfOptions = getSousFamillesForFam(criteres.famille_id);
            const hasFallback = (matrice.lignes || []).some(l => l.prix_max === null);

            return (
              <div key={matrice.matrice_id}
                className="bg-white rounded-xl border overflow-hidden"
                style={{ borderColor: isExpanded ? 'var(--primary-solid)' : 'var(--border)', transition: 'border-color 0.15s' }}>

                {/* Header de la carte */}
                <div
                  className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
                  style={{ background: isExpanded ? 'var(--primary-50)' : 'transparent' }}
                  onClick={() => setExpandedId(isExpanded ? null : matrice.matrice_id)}
                  data-testid={`matrice-card-${idx}`}>

                  {/* Badge ordre */}
                  <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                    style={{ background: 'var(--primary-solid)', color: '#fff' }}>
                    {idx + 1}
                  </span>

                  {/* Nom + résumé critères */}
                  <div className="flex-1 min-w-0">
                    <span className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{matrice.nom}</span>
                    {!isExpanded && hasCriteres && (
                      <p className="text-xs truncate mt-0.5" style={{ color: 'var(--text-muted)' }}>
                        {[cpStr && `CP: ${cpStr}`, famNom, sfNom, mrkNom].filter(Boolean).join(' · ')}
                      </p>
                    )}
                    {!isExpanded && !hasCriteres && (
                      <p className="text-xs mt-0.5 italic" style={{ color: 'var(--text-disabled)' }}>
                        Aucun critère (s'applique à tous les articles)
                      </p>
                    )}
                  </div>

                  {/* Indicateurs */}
                  <div className="flex items-center gap-2">
                    {!hasFallback && (
                      <span className="text-xs px-1.5 py-0.5 rounded-full flex items-center gap-1"
                        style={{ background: '#FEF3C7', color: '#92400E' }}>
                        <AlertCircle size={10} /> Pas de fallback
                      </span>
                    )}
                    <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{ background: 'var(--bg-subtle)', color: 'var(--text-muted)' }}>
                      {(matrice.lignes || []).length} ligne{(matrice.lignes || []).length > 1 ? 's' : ''}
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button onClick={() => moveMatrice(idx, -1)} disabled={idx === 0}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                      style={{ color: 'var(--text-muted)' }} title="Monter la priorité">
                      <ChevronUp size={14} />
                    </button>
                    <button onClick={() => moveMatrice(idx, 1)} disabled={idx === matrices.length - 1}
                      className="p-1 rounded hover:bg-gray-100 disabled:opacity-30"
                      style={{ color: 'var(--text-muted)' }} title="Baisser la priorité">
                      <ChevronDown size={14} />
                    </button>
                    <button onClick={() => removeMatrice(matrice.matrice_id)}
                      className="p-1 rounded hover:bg-red-50"
                      style={{ color: '#DC2626' }} title="Supprimer cette matrice"
                      data-testid={`remove-matrice-${idx}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Chevron */}
                  <div style={{ color: 'var(--text-muted)', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                    <ChevronDown size={16} />
                  </div>
                </div>

                {/* Corps déplié */}
                {isExpanded && (
                  <div className="border-t" style={{ borderColor: 'var(--border)' }}>
                    <div className="p-5 space-y-5">

                      {/* Nom de la matrice */}
                      <div>
                        <label className="block text-xs font-semibold mb-1.5 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                          Nom de la matrice
                        </label>
                        <input
                          value={matrice.nom}
                          onChange={e => updateMatrice(matrice.matrice_id, { nom: e.target.value })}
                          className="w-full max-w-sm px-3 py-2 rounded-lg border text-sm"
                          style={{ borderColor: 'var(--border)' }}
                          data-testid={`matrice-nom-${idx}`}
                        />
                      </div>

                      {/* Critères */}
                      <div>
                        <label className="block text-xs font-semibold mb-2 uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                          Critères (liés par ET — laisser vide = sans restriction)
                        </label>
                        <div className="grid grid-cols-2 gap-3">
                          {/* CP fournisseur */}
                          <div>
                            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>
                              CP fournisseur (préfixes, ex: 972,973)
                            </label>
                            <input
                              value={(criteres.cp_prefixes || []).join(', ')}
                              onChange={e => updateCritere(matrice.matrice_id, 'cp_prefixes',
                                e.target.value.split(',').map(s => s.trim()).filter(Boolean))}
                              placeholder="ex: 972, 973 ou laisser vide"
                              className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                              style={{ borderColor: 'var(--border)' }}
                              data-testid={`matrice-cp-${idx}`}
                            />
                          </div>
                          {/* Famille */}
                          <div>
                            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Famille</label>
                            <select
                              value={criteres.famille_id || ''}
                              onChange={e => {
                                updateCritere(matrice.matrice_id, 'famille_id', e.target.value || null);
                                updateCritere(matrice.matrice_id, 'sous_famille_id', null);
                              }}
                              className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                              style={{ borderColor: 'var(--border)', background: 'white' }}
                              data-testid={`matrice-famille-${idx}`}>
                              <option value="">— Toutes les familles —</option>
                              {familles.map(f => (
                                <option key={f.famille_id} value={f.famille_id}>{f.nom}</option>
                              ))}
                            </select>
                          </div>
                          {/* Sous-famille */}
                          <div>
                            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Sous-famille</label>
                            <select
                              value={criteres.sous_famille_id || ''}
                              onChange={e => updateCritere(matrice.matrice_id, 'sous_famille_id', e.target.value || null)}
                              className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                              style={{ borderColor: 'var(--border)', background: 'white' }}
                              data-testid={`matrice-sous-famille-${idx}`}>
                              <option value="">— Toutes les sous-familles —</option>
                              {sfOptions.map(sf => (
                                <option key={sf.sous_famille_id} value={sf.sous_famille_id}>{sf.nom}</option>
                              ))}
                            </select>
                          </div>
                          {/* Marque */}
                          <div>
                            <label className="block text-xs mb-1" style={{ color: 'var(--text-muted)' }}>Marque</label>
                            <select
                              value={criteres.marque_id || ''}
                              onChange={e => updateCritere(matrice.matrice_id, 'marque_id', e.target.value || null)}
                              className="w-full px-2.5 py-1.5 rounded-lg border text-sm"
                              style={{ borderColor: 'var(--border)', background: 'white' }}
                              data-testid={`matrice-marque-${idx}`}>
                              <option value="">— Toutes les marques —</option>
                              {marques.map(m => (
                                <option key={m.marque_id} value={m.marque_id}>{m.nom}</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Lignes de prix */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>
                            Barème de prix
                          </label>
                          <button onClick={() => addLigne(matrice.matrice_id)}
                            data-testid={`add-ligne-${idx}`}
                            className="flex items-center gap-1 text-xs font-medium hover:underline"
                            style={{ color: 'var(--primary-solid)' }}>
                            <Plus size={12} /> Ajouter une ligne
                          </button>
                        </div>
                        <div className="overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border)' }}>
                          <table className="w-full text-sm">
                            <thead>
                              <tr style={{ background: '#FAFAF8', borderBottom: '1px solid var(--border)' }}>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', width: '32%' }}>Prix achat min (€)</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', width: '32%' }}>Prix achat max (€)</th>
                                <th className="px-4 py-2.5 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', width: '28%' }}>Coefficient ×</th>
                                <th style={{ width: '8%' }} />
                              </tr>
                            </thead>
                            <tbody>
                              {(matrice.lignes || []).map((ligne, li) => {
                                const isFallback = ligne.prix_max === null;
                                return (
                                  <tr key={li} style={{ borderTop: li > 0 ? '1px solid var(--border-light)' : 'none',
                                    background: isFallback ? 'rgba(var(--primary-rgb, 79,70,229),0.03)' : 'transparent' }}>
                                    <td className="px-3 py-2">
                                      <input
                                        type="number" min={0} step="0.01"
                                        value={ligne.prix_min ?? 0}
                                        onChange={e => updateLigne(matrice.matrice_id, li, 'prix_min', parseFloat(e.target.value) || 0)}
                                        className={`${inpSm} w-full`}
                                        style={{ borderColor: 'var(--border)' }}
                                        data-testid={`ligne-min-${idx}-${li}`}
                                      />
                                    </td>
                                    <td className="px-3 py-2">
                                      {isFallback ? (
                                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs font-medium"
                                          style={{ background: 'var(--primary-50)', color: 'var(--primary-solid)' }}>
                                          <Infinity size={13} /> Infini (fallback)
                                        </div>
                                      ) : (
                                        <div className="flex items-center gap-1.5">
                                          <input
                                            type="number" min={0} step="0.01"
                                            value={ligne.prix_max ?? ''}
                                            onChange={e => updateLigne(matrice.matrice_id, li, 'prix_max',
                                              e.target.value === '' ? undefined : parseFloat(e.target.value) || 0)}
                                            placeholder="Saisir le max"
                                            className={`${inpSm} flex-1`}
                                            style={{ borderColor: 'var(--border)' }}
                                            data-testid={`ligne-max-${idx}-${li}`}
                                          />
                                          <button
                                            onClick={() => updateLigne(matrice.matrice_id, li, 'prix_max', null)}
                                            className="p-1 rounded hover:bg-gray-100 text-xs"
                                            style={{ color: 'var(--text-muted)' }} title="Définir comme fallback (∞)">
                                            <Infinity size={12} />
                                          </button>
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 py-2">
                                      <div className="flex items-center gap-1.5">
                                        <input
                                          type="number" min={0.01} step="0.01"
                                          value={ligne.coeff ?? 1}
                                          onChange={e => updateLigne(matrice.matrice_id, li, 'coeff', parseFloat(e.target.value) || 1)}
                                          className={`${inpSm} w-20`}
                                          style={{ borderColor: 'var(--border)' }}
                                          data-testid={`ligne-coeff-${idx}-${li}`}
                                        />
                                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                          {ligne.coeff > 0 ? `→ +${Math.round((ligne.coeff - 1) * 100)}%` : ''}
                                        </span>
                                      </div>
                                    </td>
                                    <td className="px-2 py-2 text-center">
                                      <button onClick={() => removeLigne(matrice.matrice_id, li)}
                                        className="p-1 rounded hover:bg-red-50"
                                        style={{ color: '#DC2626' }}
                                        data-testid={`remove-ligne-${idx}-${li}`}>
                                        <X size={13} />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}
                              {(matrice.lignes || []).length === 0 && (
                                <tr>
                                  <td colSpan={4} className="px-4 py-4 text-center text-xs italic" style={{ color: 'var(--text-disabled)' }}>
                                    Aucune ligne — ajoutez au moins une ligne de fallback (prix_max = ∞)
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                        {!hasFallback && (matrice.lignes || []).length > 0 && (
                          <p className="text-xs mt-1.5 flex items-center gap-1" style={{ color: '#92400E' }}>
                            <AlertCircle size={11} />
                            Ajoutez une ligne avec Prix max = ∞ pour garantir un calcul même si le prix dépasse le dernier seuil.
                            <button className="underline font-medium"
                              onClick={() => addLigne(matrice.matrice_id)}>
                              Ajouter la ligne fallback
                            </button>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


// ─── Onglet Commerce — Matrices TVA ─────────────────────────────────────────

function MatriceTVA({ type }) {
  const [data, setData]         = useState(null);
  const [pays, setPays]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [editCell, setEditCell] = useState(null);   // { terr_id, type_id, value }
  const [editTerr, setEditTerr] = useState(null);   // { id } ou 'new'
  const [editType, setEditType] = useState(null);   // { id } ou 'new'
  const [terrForm, setTerrForm] = useState({ designation: '', pays_code: 'FR', cp_prefixes: '' });
  const [typeForm, setTypeForm] = useState({ designation: '' });

  const load = useCallback(async () => {
    try {
      const [mRes, pRes] = await Promise.all([
        api.getTvaMatrice(type),
        api.getPays(),
      ]);
      setData(mRes.data);
      setPays(pRes.data || []);
    } catch { toast.error('Erreur chargement matrice'); }
    finally { setLoading(false); }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const cellKey = (tid, typid) => `${tid}|${typid}`;

  // ── Cellule ──
  const handleCellClick = (terr_id, type_id) => {
    const existing = data.cellules_idx[cellKey(terr_id, type_id)];
    setEditCell({ terr_id, type_id, value: existing ? String(existing.taux) : '' });
  };

  const saveCellule = async () => {
    if (!editCell) return;
    const taux = parseFloat(editCell.value);
    if (isNaN(taux)) { setEditCell(null); return; }
    try {
      await api.setTvaCellule(type, { territorialite_id: editCell.terr_id, type_id: editCell.type_id, taux });
      await load();
      toast.success('Taux enregistré');
    } catch { toast.error('Erreur'); }
    setEditCell(null);
  };

  const deleteCellule = async (terr_id, type_id) => {
    try {
      await api.deleteTvaCellule(type, terr_id, type_id);
      await load();
    } catch { toast.error('Erreur'); }
    setEditCell(null);
  };

  // ── Territorialité ──
  const startNewTerr = () => { setTerrForm({ designation: '', pays_code: 'FR', cp_prefixes: '' }); setEditTerr('new'); };
  const startEditTerr = (t) => { setTerrForm({ designation: t.designation, pays_code: t.pays_code || 'FR', cp_prefixes: t.cp_prefixes || '' }); setEditTerr(t.tva_terr_id); };

  const saveTerr = async () => {
    if (!terrForm.designation.trim()) { toast.error('Désignation requise'); return; }
    try {
      if (editTerr === 'new') {
        await api.createTvaTerritorialite({ ...terrForm, ordre: data?.territorialites?.length || 0 });
      } else {
        await api.updateTvaTerritorialite(editTerr, terrForm);
      }
      await load();
      toast.success(editTerr === 'new' ? 'Territorialité ajoutée' : 'Modifiée');
    } catch { toast.error('Erreur'); }
    setEditTerr(null);
  };

  const deleteTerr = async (id) => {
    if (!window.confirm('Supprimer cette territorialité et tous ses taux ?')) return;
    try { await api.deleteTvaTerritorialite(id); await load(); toast.success('Supprimée'); }
    catch { toast.error('Erreur'); }
  };

  // ── Type ──
  const startNewType = () => { setTypeForm({ designation: '' }); setEditType('new'); };
  const startEditType = (t) => { setTypeForm({ designation: t.designation }); setEditType(t.tva_type_id); };

  const saveType = async () => {
    if (!typeForm.designation.trim()) { toast.error('Désignation requise'); return; }
    try {
      if (editType === 'new') {
        await api.createTvaType({ ...typeForm, ordre: data?.types?.length || 0 });
      } else {
        await api.updateTvaType(editType, typeForm);
      }
      await load();
      toast.success(editType === 'new' ? 'Type ajouté' : 'Modifié');
    } catch { toast.error('Erreur'); }
    setEditType(null);
  };

  const deleteType = async (id) => {
    if (!window.confirm('Supprimer ce type de TVA et tous ses taux ?')) return;
    try { await api.deleteTvaType(id); await load(); toast.success('Supprimé'); }
    catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="py-12 text-center text-sm" style={{ color: 'var(--text-muted)' }}>Chargement…</div>;
  if (!data) return null;

  const { territorialites, types, cellules_idx } = data;

  return (
    <div className="space-y-4">
      {/* Légende */}
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Cliquez sur une cellule pour saisir le taux. Laissez vide pour indiquer une exonération non applicable.
      </p>

      {/* Matrice */}
      <div className="overflow-x-auto rounded-xl border" style={{ borderColor: 'var(--border)' }}>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr style={{ background: '#FAFAF8', borderBottom: '1px solid var(--border)' }}>
              {/* Colonne headers des territorialités */}
              <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', minWidth: 220, borderRight: '1px solid var(--border)' }}>
                Territorialité
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', width: 80, borderRight: '1px solid var(--border)' }}>
                Pays
              </th>
              <th className="px-3 py-3 text-left text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--text-muted)', width: 110, borderRight: '2px solid var(--border)' }}>
                Préfixe CP
              </th>
              {types.map((tp, i) => (
                <th key={tp.tva_type_id} className="px-3 py-3 text-center"
                  style={{ minWidth: 110, borderRight: i < types.length - 1 ? '1px solid var(--border-light)' : 'none' }}>
                  <div className="flex items-center justify-center gap-1">
                    {editType === tp.tva_type_id ? (
                      <div className="flex gap-1 items-center">
                        <input autoFocus value={typeForm.designation}
                          onChange={e => setTypeForm(f => ({ ...f, designation: e.target.value }))}
                          onKeyDown={e => e.key === 'Enter' && saveType()}
                          className="w-24 px-1.5 py-0.5 border rounded text-xs" style={{ borderColor: 'var(--border)' }} />
                        <button onClick={saveType} className="p-0.5 rounded text-green-600 hover:bg-green-50"><Check size={12} /></button>
                        <button onClick={() => setEditType(null)} className="p-0.5 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={12} /></button>
                      </div>
                    ) : (
                      <>
                        <span className="text-xs font-semibold" style={{ color: 'var(--text)' }}>{tp.designation}</span>
                        <button onClick={() => startEditType(tp)} className="p-0.5 rounded hover:bg-gray-100 opacity-50 hover:opacity-100" style={{ color: 'var(--text-muted)' }}><Pencil size={10} /></button>
                        <button onClick={() => deleteType(tp.tva_type_id)} className="p-0.5 rounded hover:bg-red-50 opacity-50 hover:opacity-100" style={{ color: '#DC2626' }}><Trash2 size={10} /></button>
                      </>
                    )}
                  </div>
                </th>
              ))}
              {/* Bouton + colonne */}
              <th className="px-2 py-3 text-center" style={{ width: 44 }}>
                {editType === 'new' ? (
                  <div className="flex gap-1 items-center justify-center">
                    <input autoFocus value={typeForm.designation} placeholder="Nouveau type"
                      onChange={e => setTypeForm(f => ({ ...f, designation: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && saveType()}
                      className="w-28 px-1.5 py-0.5 border rounded text-xs" style={{ borderColor: 'var(--border)' }} />
                    <button onClick={saveType} className="p-0.5 rounded text-green-600 hover:bg-green-50"><Check size={12} /></button>
                    <button onClick={() => setEditType(null)} className="p-0.5 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={12} /></button>
                  </div>
                ) : (
                  <button onClick={startNewType} data-testid={`add-tva-type-${type}`}
                    className="p-1 rounded-full hover:bg-gray-100" style={{ color: 'var(--primary-solid)' }}>
                    <Plus size={14} />
                  </button>
                )}
              </th>
            </tr>
          </thead>
          <tbody>
            {territorialites.map((terr, ri) => (
              <tr key={terr.tva_terr_id} style={{ borderTop: '1px solid var(--border-light)' }}>
                {/* Désignation */}
                <td className="px-3 py-2.5" style={{ borderRight: '1px solid var(--border)' }}>
                  {editTerr === terr.tva_terr_id ? (
                    <input autoFocus value={terrForm.designation}
                      onChange={e => setTerrForm(f => ({ ...f, designation: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && saveTerr()}
                      className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: 'var(--border)' }} />
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>{terr.designation}</span>
                      <button onClick={() => startEditTerr(terr)} className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><Pencil size={11} /></button>
                    </div>
                  )}
                </td>
                {/* Pays */}
                <td className="px-3 py-2.5 text-xs" style={{ color: 'var(--text-muted)', borderRight: '1px solid var(--border)' }}>
                  {editTerr === terr.tva_terr_id ? (
                    <select value={terrForm.pays_code} onChange={e => setTerrForm(f => ({ ...f, pays_code: e.target.value }))}
                      className="w-full px-1.5 py-0.5 border rounded text-xs" style={{ borderColor: 'var(--border)' }}>
                      {pays.map(p => <option key={p.code} value={p.code}>{p.code}</option>)}
                    </select>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono bg-gray-100">
                      {terr.pays_code || '—'}
                    </span>
                  )}
                </td>
                {/* Préfixe CP */}
                <td className="px-3 py-2.5" style={{ borderRight: '2px solid var(--border)' }}>
                  {editTerr === terr.tva_terr_id ? (
                    <div className="flex gap-1 items-center">
                      <input value={terrForm.cp_prefixes} placeholder="971,972…"
                        onChange={e => setTerrForm(f => ({ ...f, cp_prefixes: e.target.value }))}
                        onKeyDown={e => e.key === 'Enter' && saveTerr()}
                        className="flex-1 px-1.5 py-0.5 border rounded text-xs" style={{ borderColor: 'var(--border)' }} />
                      <button onClick={saveTerr} className="p-0.5 rounded text-green-600 hover:bg-green-50"><Check size={12} /></button>
                      <button onClick={() => setEditTerr(null)} className="p-0.5 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={12} /></button>
                      <button onClick={() => deleteTerr(terr.tva_terr_id)} className="p-0.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={12} /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-mono" style={{ color: terr.cp_prefixes ? 'var(--text)' : 'var(--text-muted)' }}>
                        {terr.cp_prefixes || <span className="italic">tout le pays</span>}
                      </span>
                      <button onClick={() => startEditTerr(terr)} className="p-0.5 rounded hover:bg-gray-100 ml-auto"
                        style={{ color: 'var(--text-muted)' }} data-testid={`edit-terr-${terr.tva_terr_id}`}><Pencil size={10} /></button>
                    </div>
                  )}
                </td>
                {/* Cellules TVA */}
                {types.map((tp, ci) => {
                  const ck = cellKey(terr.tva_terr_id, tp.tva_type_id);
                  const cell = cellules_idx[ck];
                  const isEditing = editCell?.terr_id === terr.tva_terr_id && editCell?.type_id === tp.tva_type_id;
                  return (
                    <td key={tp.tva_type_id} className="px-2 py-2 text-center"
                      style={{ borderRight: ci < types.length - 1 ? '1px solid var(--border-light)' : 'none', background: isEditing ? 'var(--primary-50)' : undefined }}>
                      {isEditing ? (
                        <div className="flex gap-1 items-center justify-center">
                          <input autoFocus value={editCell.value} placeholder="0"
                            onChange={e => setEditCell(c => ({ ...c, value: e.target.value }))}
                            onKeyDown={e => { if (e.key === 'Enter') saveCellule(); if (e.key === 'Escape') setEditCell(null); }}
                            className="w-14 px-1 py-0.5 border rounded text-xs text-center" style={{ borderColor: 'var(--primary-solid)' }} />
                          <span className="text-xs" style={{ color: 'var(--text-muted)' }}>%</span>
                          <button onClick={saveCellule} className="p-0.5 rounded text-green-600 hover:bg-green-50"><Check size={11} /></button>
                          {cell && <button onClick={() => deleteCellule(terr.tva_terr_id, tp.tva_type_id)} className="p-0.5 rounded hover:bg-red-50" style={{ color: '#DC2626' }}><Trash2 size={10} /></button>}
                          <button onClick={() => setEditCell(null)} className="p-0.5 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={11} /></button>
                        </div>
                      ) : (
                        <button onClick={() => handleCellClick(terr.tva_terr_id, tp.tva_type_id)}
                          data-testid={`cell-${type}-${ri}-${ci}`}
                          className="w-full py-1 rounded hover:bg-gray-50 transition-colors"
                          style={{ minWidth: 70 }}>
                          {cell ? (
                            <span className="text-sm font-bold" style={{ color: 'var(--primary-solid)' }}>
                              {cell.taux}%
                            </span>
                          ) : (
                            <span className="text-xs italic" style={{ color: '#D1D5DB' }}>—</span>
                          )}
                        </button>
                      )}
                    </td>
                  );
                })}
                <td />
              </tr>
            ))}

            {/* Ligne "Ajouter une territorialité" */}
            <tr style={{ borderTop: '1px solid var(--border-light)', background: '#FAFAF8' }}>
              {editTerr === 'new' ? (
                <>
                  <td className="px-3 py-2" style={{ borderRight: '1px solid var(--border)' }}>
                    <input autoFocus placeholder="Désignation" value={terrForm.designation}
                      onChange={e => setTerrForm(f => ({ ...f, designation: e.target.value }))}
                      onKeyDown={e => e.key === 'Enter' && saveTerr()}
                      className="w-full px-2 py-1 border rounded text-sm" style={{ borderColor: 'var(--primary-solid)' }} />
                  </td>
                  <td className="px-3 py-2" style={{ borderRight: '1px solid var(--border)' }}>
                    <select value={terrForm.pays_code} onChange={e => setTerrForm(f => ({ ...f, pays_code: e.target.value }))}
                      className="w-full px-1.5 py-1 border rounded text-xs" style={{ borderColor: 'var(--border)' }}>
                      {pays.map(p => <option key={p.code} value={p.code}>{p.code} — {p.nom}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2" style={{ borderRight: '2px solid var(--border)' }}>
                    <div className="flex gap-1 items-center">
                      <input placeholder="ex: 971,972" value={terrForm.cp_prefixes}
                        onChange={e => setTerrForm(f => ({ ...f, cp_prefixes: e.target.value }))}
                        className="flex-1 px-2 py-1 border rounded text-xs" style={{ borderColor: 'var(--border)' }} />
                      <button onClick={saveTerr} className="p-1 rounded text-green-600 hover:bg-green-50"><Check size={13} /></button>
                      <button onClick={() => setEditTerr(null)} className="p-1 rounded hover:bg-gray-100" style={{ color: 'var(--text-muted)' }}><X size={13} /></button>
                    </div>
                  </td>
                  <td colSpan={types.length + 1} />
                </>
              ) : (
                <td colSpan={types.length + 4} className="px-3 py-2">
                  <button onClick={startNewTerr} data-testid={`add-tva-terr-${type}`}
                    className="flex items-center gap-1.5 text-sm font-medium hover:text-blue-700 transition-colors"
                    style={{ color: 'var(--primary-solid)' }}>
                    <Plus size={14} /> Ajouter une territorialité
                  </button>
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Légende règle CP vide */}
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        <Globe size={11} className="inline mr-1 opacity-60" />
        Préfixe CP vide = s'applique à tout le pays (sauf si une territorialité plus spécifique existe).
        Exemple : <code className="px-1 bg-gray-100 rounded">971</code> s'applique à tous les CP commençant par <code className="px-1 bg-gray-100 rounded">971</code>.
      </p>
    </div>
  );
}


function CommerceTab() {
  const [subTab, setSubTab] = useState('vente');

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="font-semibold text-sm" style={{ color: 'var(--text)',  }}>Matrices TVA</h3>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
              Taux applicables par territorialité (déterminée depuis le code postal de l'agence émettrice).
            </p>
          </div>
        </div>

        {/* Sous-onglets Ventes / Achats */}
        <div className="flex gap-1 mb-5 border-b" style={{ borderColor: 'var(--border-light)' }}>
          {[{ key: 'vente', label: 'TVA Ventes' }, { key: 'achat', label: 'TVA Achats' }].map(({ key, label }) => (
            <button key={key} onClick={() => setSubTab(key)}
              data-testid={`tva-tab-${key}`}
              className="px-4 py-2 text-sm font-medium border-b-2 transition-colors -mb-px"
              style={{
                borderBottomColor: subTab === key ? 'var(--primary-solid)' : 'transparent',
                color: subTab === key ? 'var(--primary-solid)' : 'var(--text-muted)',
              }}>
              {label}
            </button>
          ))}
        </div>

        <MatriceTVA key={subTab} type={subTab} />
      </div>
    </div>
  );
}

// ─── Onglet Location ──────────────────────────────────────────────────────────
function LocationTab({ parametres, onSave }) {
  const [articleAssuranceId, setArticleAssuranceId] = useState('');
  const [toleranceBL, setToleranceBL]               = useState(0);
  const [heureGraceRetour, setHeureGraceRetour]     = useState('');
  const [articles, setArticles]                     = useState([]);
  const [motifs, setMotifs]                         = useState([]);
  const [newMotif, setNewMotif]                     = useState('');
  const [saving, setSaving]                         = useState(false);
  // Edition inline
  const [editingId, setEditingId]                   = useState(null);
  const [editingVal, setEditingVal]                 = useState('');
  // Modale de confirmation suppression
  const [confirmMotif, setConfirmMotif]             = useState(null); // { motif_id, libelle }

  useEffect(() => {
    setArticleAssuranceId(parametres?.article_assurance_id || '');
    setToleranceBL(parametres?.terrain_bl_date_tolerance ?? 0);
    const hg = parametres?.heure_grace_retour;
    setHeureGraceRetour(hg != null ? String(hg) : '');
    api.getArticles({ type: 'location', per_page: 200 })
      .then(r => setArticles(r.data?.items || r.data || []))
      .catch(() => {});
    api.getMotifsSuspension()
      .then(r => setMotifs(r.data || []))
      .catch(() => {});
  }, [parametres]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave({
        article_assurance_id:      articleAssuranceId || null,
        terrain_bl_date_tolerance: Number(toleranceBL) || 0,
        heure_grace_retour:        heureGraceRetour !== '' ? Number(heureGraceRetour) : null,
      });
      toast.success('Paramètres enregistrés');
    } catch {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const addMotif = async () => {
    const lib = newMotif.trim();
    if (!lib) return;
    try {
      const r = await api.createMotifSuspension({ libelle: lib });
      setMotifs(prev => [...prev, r.data]);
      setNewMotif('');
      toast.success('Motif ajouté');
    } catch { toast.error('Erreur'); }
  };

  const startEdit = (m) => {
    setEditingId(m.motif_id);
    setEditingVal(m.libelle);
  };

  const cancelEdit = () => { setEditingId(null); setEditingVal(''); };

  const saveEdit = async (motif_id) => {
    const lib = editingVal.trim();
    if (!lib) return;
    try {
      const r = await api.updateMotifSuspension(motif_id, { libelle: lib });
      setMotifs(prev => prev.map(m => m.motif_id === motif_id ? r.data : m));
      cancelEdit();
      toast.success('Motif modifié');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const confirmDelete = (m) => setConfirmMotif(m);

  const doDelete = async () => {
    if (!confirmMotif) return;
    try {
      await api.deleteMotifSuspension(confirmMotif.motif_id);
      setMotifs(prev => prev.filter(m => m.motif_id !== confirmMotif.motif_id));
      toast.success('Motif supprimé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    } finally {
      setConfirmMotif(null);
    }
  };

  return (
    <div className="space-y-6 max-w-lg">
      {/* Article assurance */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-sm font-bold mb-0.5" style={{ color: 'var(--text)' }}>Article assurance</h3>
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
          Article de type Location utilisé pour calculer la ligne assurance (8% du total location) sur les contrats.
        </p>
        <select
          value={articleAssuranceId}
          onChange={e => setArticleAssuranceId(e.target.value)}
          data-testid="article-assurance-select"
          className="w-full px-3 py-2 rounded-lg border text-sm"
          style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}>
          <option value="">— Aucun article sélectionné —</option>
          {articles.map(a => (
            <option key={a.article_id} value={a.article_id}>
              {a.reference ? `[${a.reference}] ` : ''}{a.designation}
            </option>
          ))}
        </select>
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving}
            data-testid="save-article-assurance-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>

      </div>

      {/* Tolérance date BL terrain */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-sm font-bold mb-0.5" style={{ color: 'var(--text)' }}>Tolérance de date — Bons de livraison terrain</h3>
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
          Nombre de jours (±) que l'agent terrain peut saisir comme date réelle. <strong>0</strong> = date de signature automatique.
        </p>
        <div className="flex items-center gap-3">
          <input type="number" min={0} max={30} value={toleranceBL}
            onChange={e => setToleranceBL(Math.max(0, Math.min(30, Number(e.target.value) || 0)))}
            data-testid="tolerance-bl-input"
            className="w-24 px-3 py-2 rounded-lg border text-sm text-center"
            style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }} />
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {toleranceBL === 0 ? 'Désactivé' : `±${toleranceBL} jour${toleranceBL > 1 ? 's' : ''}`}
          </span>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving}
            data-testid="save-tolerance-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Heure de grâce — retours location */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-sm font-bold mb-0.5" style={{ color: 'var(--text)' }}>Heure de grâce — retours</h3>
        <p className="text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
          Si le BL retour est signé <strong>avant cette heure</strong> (heure locale de l'agence), la journée n'est
          pas facturée au client : la date de retour effective recule d'un jour.
          Laissez vide pour désactiver.
        </p>
        <div className="flex items-center gap-3">
          <div className="relative">
            <input
              type="number" min={0} max={23}
              value={heureGraceRetour}
              placeholder="—"
              onChange={e => {
                const v = e.target.value;
                if (v === '') { setHeureGraceRetour(''); return; }
                setHeureGraceRetour(String(Math.max(0, Math.min(23, Number(v) || 0))));
              }}
              data-testid="heure-grace-retour-input"
              className="w-24 px-3 py-2 rounded-lg border text-sm text-center"
              style={{ borderColor: 'var(--border)', background: 'white', color: 'var(--text)' }}
            />
          </div>
          <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
            {heureGraceRetour === ''
              ? 'Désactivé'
              : `Retours avant ${String(heureGraceRetour).padStart(2,'0')}h00 → facturés J-1`}
          </span>
        </div>
        <div className="flex justify-end mt-3">
          <button onClick={save} disabled={saving}
            data-testid="save-heure-grace-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-gradient)' }}>
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Motifs de suspension */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-sm font-bold mb-0.5" style={{ color: 'var(--text)' }}>Motifs de suspension</h3>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
          Liste des motifs disponibles lors de l'ajout d'une suspension sur un contrat.
        </p>
        <div className="space-y-2 mb-3">
          {motifs.length === 0 && (
            <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Aucun motif configuré</p>
          )}
          {motifs.map(m => (
            <div key={m.motif_id} className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
              {editingId === m.motif_id ? (
                <>
                  <input
                    autoFocus
                    value={editingVal}
                    onChange={e => setEditingVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(m.motif_id); if (e.key === 'Escape') cancelEdit(); }}
                    data-testid={`edit-motif-input-${m.motif_id}`}
                    className="flex-1 px-2 py-0.5 rounded border text-sm"
                    style={{ borderColor: 'var(--primary-solid)', outline: 'none' }}
                  />
                  <button onClick={() => saveEdit(m.motif_id)}
                    data-testid={`save-edit-motif-${m.motif_id}`}
                    title="Enregistrer"
                    className="p-1.5 rounded transition-colors hover:opacity-80"
                    style={{ color: 'white', background: 'var(--primary-solid)' }}>
                    <Check size={13} />
                  </button>
                  <button onClick={cancelEdit}
                    title="Annuler"
                    className="p-1.5 rounded transition-colors hover:opacity-70"
                    style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)' }}>
                    <X size={13} />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm" style={{ color: 'var(--text)' }}>{m.libelle}</span>
                  <button onClick={() => startEdit(m)}
                    data-testid={`edit-motif-${m.motif_id}`}
                    title="Modifier ce motif"
                    className="p-1.5 rounded transition-colors hover:opacity-80"
                    style={{ color: 'var(--primary-solid)', background: 'transparent' }}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => confirmDelete(m)}
                    data-testid={`delete-motif-${m.motif_id}`}
                    title="Supprimer ce motif"
                    className="p-1.5 rounded transition-colors hover:opacity-80"
                    style={{ color: '#ef4444', background: 'transparent' }}>
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newMotif} onChange={e => setNewMotif(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addMotif()}
            placeholder="Nouveau motif…"
            data-testid="new-motif-input"
            className="flex-1 px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)' }} />
          <button onClick={addMotif}
            data-testid="add-motif-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-solid)' }}>
            Ajouter
          </button>
        </div>
      </div>

      {/* Modale confirmation suppression */}
      {confirmMotif && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setConfirmMotif(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-80 mx-4"
            onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#fee2e2' }}>
                <Trash2 size={18} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Supprimer le motif ?</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Cette action est irréversible.</p>
              </div>
            </div>
            <p className="text-sm mb-5 px-1 py-2 rounded-lg text-center font-medium"
              style={{ background: 'var(--bg-subtle)', color: 'var(--text)', border: '1px solid var(--border)' }}>
              « {confirmMotif.libelle} »
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmMotif(null)}
                data-testid="cancel-delete-motif"
                className="px-4 py-2 rounded-lg text-sm border transition-colors hover:opacity-80"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
                Annuler
              </button>
              <button onClick={doDelete}
                data-testid="confirm-delete-motif"
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90"
                style={{ background: '#ef4444' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── Placeholder tab ──────────────────────────────────────────────────────────
// ─── Onglet Atelier ───────────────────────────────────────────────────────────
function AtelierTab() {
  const [motifs,      setMotifs]      = useState([]);
  const [newMotif,    setNewMotif]    = useState('');
  const [editingId,   setEditingId]   = useState(null);
  const [editingVal,  setEditingVal]  = useState('');
  const [confirmMotif, setConfirmMotif] = useState(null);

  useEffect(() => {
    api.getMotifsNonFacturation()
      .then(r => setMotifs(r.data || []))
      .catch(() => {});
  }, []);

  const addMotif = async () => {
    const lib = newMotif.trim();
    if (!lib) return;
    try {
      const r = await api.createMotifNonFacturation({ libelle: lib });
      setMotifs(prev => [...prev, r.data]);
      setNewMotif('');
      toast.success('Motif ajouté');
    } catch { toast.error('Erreur lors de l\'ajout'); }
  };

  const startEdit = (m) => { setEditingId(m.motif_id); setEditingVal(m.libelle); };
  const cancelEdit = () => { setEditingId(null); setEditingVal(''); };

  const saveEdit = async (motif_id) => {
    const lib = editingVal.trim();
    if (!lib) return;
    try {
      const r = await api.updateMotifNonFacturation(motif_id, { libelle: lib });
      setMotifs(prev => prev.map(m => m.motif_id === motif_id ? r.data : m));
      cancelEdit();
      toast.success('Motif modifié');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
  };

  const doDelete = async () => {
    if (!confirmMotif) return;
    try {
      await api.deleteMotifNonFacturation(confirmMotif.motif_id);
      setMotifs(prev => prev.filter(m => m.motif_id !== confirmMotif.motif_id));
      toast.success('Motif supprimé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erreur'); }
    finally { setConfirmMotif(null); }
  };

  return (
    <div className="space-y-6 max-w-lg">
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: 'var(--border)' }}>
        <h3 className="text-sm font-bold mb-0.5" style={{ color: 'var(--text)' }}>Motifs de non-facturation</h3>
        <p className="text-xs mb-4" style={{ color: 'var(--text-muted)' }}>
          Liste des motifs disponibles pour justifier une déduction d'heures sur un ordre de réparation (malfaçon, dépassement, garantie...).
        </p>
        <div className="space-y-2 mb-3">
          {motifs.length === 0 && (
            <p className="text-xs italic" style={{ color: 'var(--text-muted)' }}>Aucun motif configuré</p>
          )}
          {motifs.map(m => (
            <div key={m.motif_id} className="flex items-center gap-2 px-3 py-2 rounded-lg"
              style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border)' }}>
              {editingId === m.motif_id ? (
                <>
                  <input
                    autoFocus
                    value={editingVal}
                    onChange={e => setEditingVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(m.motif_id); if (e.key === 'Escape') cancelEdit(); }}
                    data-testid={`edit-motif-nf-input-${m.motif_id}`}
                    className="flex-1 px-2 py-0.5 rounded border text-sm"
                    style={{ borderColor: 'var(--primary-solid)', outline: 'none' }}
                  />
                  <button onClick={() => saveEdit(m.motif_id)} data-testid={`save-edit-motif-nf-${m.motif_id}`}
                    title="Enregistrer" className="p-1.5 rounded transition-colors hover:opacity-80"
                    style={{ color: 'white', background: 'var(--primary-solid)' }}>
                    <Check size={13} />
                  </button>
                  <button onClick={cancelEdit} title="Annuler"
                    className="p-1.5 rounded transition-colors hover:opacity-70"
                    style={{ color: 'var(--text-muted)', background: 'var(--bg-subtle)' }}>
                    <X size={13} />
                  </button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm" style={{ color: 'var(--text)' }}>{m.libelle}</span>
                  <button onClick={() => startEdit(m)} data-testid={`edit-motif-nf-${m.motif_id}`}
                    title="Modifier" className="p-1.5 rounded transition-colors hover:opacity-80"
                    style={{ color: 'var(--primary-solid)', background: 'transparent' }}>
                    <Pencil size={13} />
                  </button>
                  <button onClick={() => setConfirmMotif(m)} data-testid={`delete-motif-nf-${m.motif_id}`}
                    title="Supprimer" className="p-1.5 rounded transition-colors hover:opacity-80"
                    style={{ color: '#ef4444', background: 'transparent' }}>
                    <Trash2 size={13} />
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <input value={newMotif} onChange={e => setNewMotif(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addMotif()}
            placeholder="Nouveau motif de non-facturation…"
            data-testid="new-motif-nf-input"
            className="flex-1 px-3 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border)' }} />
          <button onClick={addMotif} data-testid="add-motif-nf-btn"
            className="px-4 py-2 rounded-lg text-sm font-semibold text-white"
            style={{ background: 'var(--primary-solid)' }}>
            Ajouter
          </button>
        </div>
      </div>

      {/* Confirmation suppression */}
      {confirmMotif && (
        <div className="fixed inset-0 z-50 flex items-center justify-center"
          style={{ background: 'rgba(0,0,0,0.4)' }}
          onClick={() => setConfirmMotif(null)}>
          <div className="bg-white rounded-xl shadow-xl p-6 w-80 mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: '#fee2e2' }}>
                <Trash2 size={18} style={{ color: '#ef4444' }} />
              </div>
              <div>
                <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>Supprimer le motif ?</p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Cette action est irréversible.</p>
              </div>
            </div>
            <p className="text-sm mb-5 px-1 py-2 rounded-lg text-center font-medium"
              style={{ background: 'var(--bg-subtle)', color: 'var(--text)', border: '1px solid var(--border)' }}>
              « {confirmMotif.libelle} »
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmMotif(null)} data-testid="cancel-delete-motif-nf"
                className="px-4 py-2 rounded-lg text-sm border transition-colors hover:opacity-80"
                style={{ borderColor: 'var(--border)', color: 'var(--text)' }}>
                Annuler
              </button>
              <button onClick={doDelete} data-testid="confirm-delete-motif-nf"
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors hover:opacity-90"
                style={{ background: '#ef4444' }}>
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PlaceholderTab({ label }) {
  return (
    <div className="bg-white rounded-xl border p-12 text-center" style={{ borderColor: 'var(--border)' }}>
      <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3"
        style={{ background: 'var(--primary-50)' }}>
        <ChevronRight size={20} style={{ color: 'var(--primary-solid)' }} />
      </div>
      <p className="text-sm font-medium mb-1" style={{ color: 'var(--text)' }}>
        Paramètres {label}
      </p>
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        Cette section sera disponible prochainement.
      </p>
    </div>
  );
}

// ─── Page principale ──────────────────────────────────────────────────────────
function PushNotificationsSection() {
  const [permission, setPermission] = useState(() => getPushPermission());
  const [subscribed, setSubscribed] = useState(false);
  const [loading, setLoading]       = useState(false);

  useEffect(() => {
    if (!isPushSupported()) return;
    isPushSubscribed().then(setSubscribed);
  }, []);

  const handleEnable = async () => {
    setLoading(true);
    const result = await subscribePush();
    setPermission(getPushPermission());
    setSubscribed(result === 'granted');
    setLoading(false);
    if (result === 'granted') toast.success('Notifications activées');
    else if (result === 'denied') toast.error('Permission refusée — vérifiez les paramètres du navigateur');
  };

  const handleDisable = async () => {
    setLoading(true);
    await unsubscribePush();
    setSubscribed(false);
    setLoading(false);
    toast.info('Notifications désactivées');
  };

  if (!isPushSupported()) return null;

  return (
    <div className="mt-8 pt-6" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text)' }}>
            <Bell size={14} style={{ color: '#1D4ED8' }} />
            Notifications push
          </h3>
          <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {permission === 'denied'
              ? 'Bloqué par le navigateur — autorisez dans les paramètres système'
              : 'Recevez les rappels et messages même lorsque l\'app est fermée'}
          </p>
        </div>
        {permission === 'denied' ? (
          <span className="text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5"
            style={{ background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECACA' }}>
            <BellOff size={12} /> Bloqué
          </span>
        ) : subscribed ? (
          <button onClick={handleDisable} disabled={loading} data-testid="push-disable-btn"
            className="text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors hover:bg-gray-100"
            style={{ background: '#F0FDF4', color: '#16A34A', border: '1px solid #BBF7D0', cursor: 'pointer' }}>
            <Bell size={12} fill="#16A34A" /> Activé — Désactiver
          </button>
        ) : (
          <button onClick={handleEnable} disabled={loading} data-testid="push-enable-btn"
            className="text-xs font-medium px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors hover:bg-blue-50"
            style={{ background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE', cursor: 'pointer' }}>
            <Bell size={12} /> {loading ? 'Activation…' : 'Activer les notifications'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function ParametresGeneraux() {
  const [activeTab, setActiveTab] = useState('annuaire');
  const [parametres, setParametres] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.getParametres().then(res => setParametres(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleSave = async (updates) => {
    const res = await api.updateParametres(updates);
    setParametres(res.data);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-6 h-6 border-2 border-t-transparent rounded-full" style={{ borderColor: 'var(--primary-solid)', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-lg font-bold" style={{ color: 'var(--text)',  }}>Paramètres généraux</h1>
        <p className="text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
          Configuration par module de l'application
        </p>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 mb-6 border-b" style={{ borderColor: 'var(--border)' }}>
        {TABS.map(({ key, label, icon: Icon }) => (
          <button key={key} type="button"
            onClick={() => setActiveTab(key)}
            data-testid={`tab-${key}`}
            className="flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px"
            style={{
              borderBottomColor: activeTab === key ? 'var(--primary-solid)' : 'transparent',
              color: activeTab === key ? 'var(--primary-solid)' : 'var(--text-muted)',
            }}>
            <Icon size={14} />
            {label}
          </button>
        ))}
      </div>

      {/* Contenu */}
      {activeTab === 'annuaire'  && <AnnuaireTab parametres={parametres} onSave={handleSave} />}
      {activeTab === 'catalogue' && <CatalogueTab />}
      {activeTab === 'commerce'  && <CommerceTab />}
      {activeTab === 'location'  && <LocationTab parametres={parametres} onSave={handleSave} />}
      {activeTab === 'atelier'   && <AtelierTab />}
      {activeTab === 'rh'        && <FonctionsInternes />}
      {activeTab !== 'annuaire' && activeTab !== 'catalogue' && activeTab !== 'commerce' && activeTab !== 'location' && activeTab !== 'atelier' && activeTab !== 'rh' && <PlaceholderTab label={TABS.find(t => t.key === activeTab)?.label} />}

      {/* ── Notifications Push ─────────────────────────────────────────────── */}
      <PushNotificationsSection />
    </div>
  );
}
